import React, { useState } from 'react'
import { Form, Input, InputNumber, DatePicker, Button, Space, Spin, Alert } from 'antd'
import dayjs from 'dayjs'
import { apiClient } from '@services/api'

interface DailyReportFormProps {
  operationId: string
  onSuccess?: () => void
  onCancel?: () => void
}

export const DailyReportForm: React.FC<DailyReportFormProps> = ({
  operationId,
  onSuccess,
  onCancel,
}) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (values: any) => {
    try {
      setLoading(true)
      setError(null)

      const payload = {
        operation_id: operationId,
        report_date: values.report_date?.toISOString(),
        summary: values.summary,
        weather_conditions: values.weather_conditions,
        total_productive_hours: values.total_productive_hours || 0,
        total_wait_time_hours: values.total_wait_time_hours || 0,
        total_npt_hours: values.total_npt_hours || 0,
        environmental_notes: values.environmental_notes,
      }

      await apiClient.createDailyReport(payload)

      form.resetFields()
      setLoading(false)

      if (onSuccess) {
        onSuccess()
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create daily report')
      setLoading(false)
    }
  }

  return (
    <div style={{ padding: '20px 0' }}>
      {error && <Alert message={error} type="error" style={{ marginBottom: '16px' }} closable />}

      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        disabled={loading}
      >
        <Form.Item
          name="report_date"
          label="Report Date"
          rules={[{ required: true, message: 'Please select a date' }]}
          initialValue={dayjs()}
        >
          <DatePicker showTime format="YYYY-MM-DD HH:mm" />
        </Form.Item>

        <Form.Item
          name="summary"
          label="Summary"
          rules={[{ required: true, message: 'Please enter a summary' }]}
        >
          <Input.TextArea rows={4} placeholder="Summary of daily operations and activities..." />
        </Form.Item>

        <Form.Item
          name="weather_conditions"
          label="Weather Conditions"
          rules={[{ required: true, message: 'Please enter weather conditions' }]}
        >
          <Input placeholder="e.g., Clear, temp 22°C" />
        </Form.Item>

        <Form.Item
          name="total_productive_hours"
          label="Productive Hours"
          rules={[{ required: true, message: 'Please enter productive hours' }]}
          initialValue={0}
        >
          <InputNumber min={0} max={24} step={0.5} />
        </Form.Item>

        <Form.Item
          name="total_wait_time_hours"
          label="Wait Time Hours"
          initialValue={0}
        >
          <InputNumber min={0} max={24} step={0.5} />
        </Form.Item>

        <Form.Item
          name="total_npt_hours"
          label="Non-Productive Time (NPT) Hours"
          initialValue={0}
        >
          <InputNumber min={0} max={24} step={0.5} />
        </Form.Item>

        <Form.Item
          name="environmental_notes"
          label="Environmental Notes (Optional)"
        >
          <Input.TextArea rows={3} placeholder="Additional environmental or operational notes..." />
        </Form.Item>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={loading}>
              Create Daily Report
            </Button>
            {onCancel && <Button onClick={onCancel}>Cancel</Button>}
          </Space>
        </Form.Item>
      </Form>
    </div>
  )
}

export default DailyReportForm
